import React, { useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import Modal from "../../../components/Modal/Modal";
import Titulo from "../../../components/Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { AssinaturaApi } from "../../../hooks/assinaturaApi";
import { DadosCartao, FormaPagamento } from "../../../types/Assinatura";
import { ViewModalPagamento } from "../styles";

interface ModalMudarFormaProps {
  isOpen: boolean;
  setOpen: (v: boolean) => void;
  formaAtual: FormaPagamento;
  onSucesso: () => void;
}

/**
 * Modal pra cliente mudar forma de pagamento padrao (cartao <-> PIX).
 * Comportamento back: §3.7 do planejamento-pix.
 */
export const ModalMudarForma: React.FC<ModalMudarFormaProps> = ({
  isOpen,
  setOpen,
  formaAtual,
  onSucesso,
}) => {
  const { theme } = useTheme();

  // Forma alvo: se atual=cartao, mostra opcao PIX (e vice-versa)
  const formaAlvo: FormaPagamento = formaAtual === "cartao" ? "pix" : "cartao";

  const [cartao, setCartao] = useState<DadosCartao>({
    holder_name: "",
    number: "",
    exp_month: 0,
    exp_year: 0,
    cvv: "",
  });
  const [validadeStr, setValidadeStr] = useState("");
  const [processando, setProcessando] = useState(false);

  const handleValidadeChange = (v: string) => {
    const limpo = v.replace(/\D/g, "").slice(0, 4);
    const formatado =
      limpo.length > 2 ? `${limpo.slice(0, 2)}/${limpo.slice(2)}` : limpo;
    setValidadeStr(formatado);
    if (limpo.length >= 2) {
      const mes = parseInt(limpo.slice(0, 2), 10);
      setCartao((c) => ({ ...c, exp_month: mes }));
    }
    if (limpo.length === 4) {
      const ano = 2000 + parseInt(limpo.slice(2, 4), 10);
      setCartao((c) => ({ ...c, exp_year: ano }));
    }
  };

  const handleConfirmar = async () => {
    if (formaAlvo === "cartao") {
      if (
        !cartao.holder_name ||
        !cartao.number ||
        !cartao.exp_month ||
        !cartao.exp_year ||
        !cartao.cvv
      ) {
        Swal.fire({ icon: "warning", title: "Preencha todos os campos do cartão", timer: 2000 });
        return;
      }
    }

    setProcessando(true);
    try {
      const res = await AssinaturaApi().mudarFormaPagamento(
        formaAlvo,
        formaAlvo === "cartao" ? cartao : undefined
      );

      if (formaAlvo === "cartao") {
        // Cartao: pode ter cobranca sincrona
        if (res.ok && res.cobrou && res.aprovado) {
          setOpen(false);
          await Swal.fire({ icon: "success", title: "Forma alterada e pagamento aprovado!", text: res.mensagem });
          onSucesso();
        } else if (res.ok && !res.cobrou) {
          setOpen(false);
          await Swal.fire({ icon: "success", title: "Forma de pagamento alterada", text: res.mensagem });
          onSucesso();
        } else {
          Swal.fire({
            icon: "error",
            title: "Pagamento não autorizado",
            text: res.mensagem || "Cartão recusado. Tente outro ou pague via PIX.",
          });
        }
      } else {
        // PIX: nunca cobra, so muda forma + gera PIX no ciclo aberto
        setOpen(false);
        await Swal.fire({ icon: "success", title: "Forma alterada para PIX", text: res.mensagem });
        onSucesso();
      }
    } catch (err: any) {
      console.error("Erro mudar forma:", err);
      Swal.fire({
        icon: "error",
        title: "Erro ao processar",
        text: err?.response?.data?.erro || "Não foi possível alterar a forma de pagamento.",
      });
    } finally {
      setProcessando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setModal={setOpen}
      width={"70rem"}
      align="center"
      justify="center"
      animation="opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)"
      margin="0rem 2rem"
      padding={"3rem"}
    >
      <Titulo
        justifyContent="center"
        flexDirection="column-reverse"
        titulo={formaAlvo === "cartao" ? "Mudar para cartão" : "Mudar para PIX"}
        subtitulo={
          formaAlvo === "cartao"
            ? "Cadastre seu cartão. A próxima cobrança será no cartão."
            : "Próxima cobrança gerará um PIX para pagamento."
        }
      />
      <ViewModalPagamento theme={theme}>
        {formaAlvo === "cartao" && (
          <div className="form-cartao">
            <Input
              type="text"
              label="Nome impresso no cartão"
              value={cartao.holder_name}
              onChange={(v) =>
                setCartao((c) => ({ ...c, holder_name: v.toUpperCase() }))
              }
              placeholder="Nome completo"
              ignoreValidations
            />
            <Input
              type="text"
              label="Número do cartão"
              value={cartao.number}
              onChange={(v) =>
                setCartao((c) => ({ ...c, number: v.replace(/\D/g, "") }))
              }
              placeholder="0000 0000 0000 0000"
              maxLength={19}
              showMaxLength={false}
              ignoreValidations
            />
            <div className="linha-dupla">
              <Input
                type="text"
                label="Validade (MM/AA)"
                value={validadeStr}
                onChange={(v) => handleValidadeChange(v)}
                placeholder="MM/AA"
                maxLength={5}
                showMaxLength={false}
                ignoreValidations
              />
              <Input
                type="text"
                label="CVV"
                value={cartao.cvv}
                onChange={(v) =>
                  setCartao((c) => ({ ...c, cvv: v.replace(/\D/g, "") }))
                }
                placeholder="123"
                maxLength={4}
                showMaxLength={false}
                ignoreValidations
              />
            </div>
          </div>
        )}

        {formaAlvo === "pix" && (
          <div className="pix-aviso fonteTexto" style={{ textAlign: "center", padding: "1rem" }}>
            Você passará a pagar via PIX. Se houver uma fatura em aberto, geraremos
            um PIX automaticamente para você pagar. Sem cobrança no cartão.
          </div>
        )}

        <div className="acoes-modal">
          <Button
            title="Cancelar"
            className="button cinza fonteTexto"
            onClick={() => setOpen(false)}
            width="auto"
            padding="0 2rem"
          />
          <Button
            title={processando ? "Processando..." : "Confirmar"}
            className="fonteTexto"
            onClick={handleConfirmar}
            width="auto"
            padding="0 2rem"
            loading={processando}
          />
        </div>
      </ViewModalPagamento>
    </Modal>
  );
};

export default ModalMudarForma;
